import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError, errorMessage, PASSWORD_REQUIRED_EVENT, setToken } from './api';
import type { Me, Warehouse } from './types';

const TOKEN_KEY = 'wms.token';
const WAREHOUSE_KEY = 'wms.warehouse';

/** localStorage may be unavailable (private mode, kiosk policies): the app then simply forgets. */
export const store = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string | null): void {
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    } catch {
      // not persisted
    }
  },
};

export interface SessionValue {
  readonly me: Me;
  readonly warehouses: readonly Warehouse[];
  readonly warehouseId: string | null;
  readonly isOperator: boolean;
  /** admins and supervisors: station setup, exception decisions */
  readonly isSupervisor: boolean;
  readonly isAdmin: boolean;
  setWarehouseId(id: string): void;
  /** reads the signed-in user and the warehouses again (after a password change, a new warehouse) */
  reload(): Promise<void>;
  /** goes on with a new token (a password change ends the old one) */
  renew(token: string): void;
  logout(): void;
}

/** Forgets the session kept in this browser (after a password reset by link, the next one signs in afresh). */
export function forgetSavedSession(): void {
  store.set(TOKEN_KEY, null);
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession outside SessionGate');
  return value;
}

/** The session where there may be none (shared building blocks). */
export function useOptionalSession(): SessionValue | null {
  return useContext(SessionContext);
}

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'out' }
  // a saved session the server could not confirm yet (no network, a restart while deploying): kept, tried again
  | { readonly kind: 'unreachable'; readonly message: string }
  | { readonly kind: 'in'; readonly me: Me; readonly warehouses: readonly Warehouse[] };

/** The server said this session is over (signed out, password changed, user turned off). */
const refused = (error: unknown) => error instanceof ApiError && (error.status === 401 || error.status === 403);

/** Restores the saved session, or shows the login screen until there is one. */
export function SessionGate(props: { children: ReactNode; login: (onToken: (token: string) => Promise<void>) => ReactNode; loading: ReactNode }) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [warehouseId, setWarehouse] = useState<string | null>(() => store.get(WAREHOUSE_KEY));

  const logout = useCallback(() => {
    store.set(TOKEN_KEY, null);
    setToken(null);
    setState({ kind: 'out' });
  }, []);

  const reload = useCallback(async () => {
    const [me, warehouses] = await Promise.all([api<Me>('GET', '/api/auth/me'), api<Warehouse[]>('GET', '/api/warehouses')]);
    setState((s) => (s.kind === 'in' ? { ...s, me, warehouses } : s));
  }, []);

  const start = useCallback(
    async (token: string) => {
      setToken(token, logout);
      try {
        const [me, warehouses] = await Promise.all([api<Me>('GET', '/api/auth/me'), api<Warehouse[]>('GET', '/api/warehouses')]);
        store.set(TOKEN_KEY, token);
        setWarehouse((current) => (current && warehouses.some((w) => w.id === current) ? current : (warehouses[0]?.id ?? null)));
        setState({ kind: 'in', me, warehouses });
      } catch (error) {
        // only the server's no ends a session; a network or server hiccup leaves the saved one in place
        if (refused(error)) logout();
        else setToken(null);
        throw error;
      }
    },
    [logout],
  );

  // the saved session at start-up: an unreachable server is waited for, not taken as a sign-out
  const restore = useCallback(
    (token: string) =>
      start(token).catch((error: unknown) => {
        if (!refused(error) && store.get(TOKEN_KEY) === token) setState({ kind: 'unreachable', message: errorMessage(error) });
      }),
    [start],
  );

  useEffect(() => {
    const onRequired = () => void reload().catch(() => undefined);
    window.addEventListener(PASSWORD_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(PASSWORD_REQUIRED_EVENT, onRequired);
  }, [reload]);

  useEffect(() => {
    const saved = store.get(TOKEN_KEY);
    if (saved) void restore(saved);
    else setState({ kind: 'out' });
  }, [restore]);

  // tried again every few seconds while the server cannot be reached
  useEffect(() => {
    if (state.kind !== 'unreachable') return;
    const saved = store.get(TOKEN_KEY);
    if (!saved) return;
    const timer = setTimeout(() => void restore(saved), 5_000);
    return () => clearTimeout(timer);
  }, [state, restore]);

  const value = useMemo<SessionValue | null>(() => {
    if (state.kind !== 'in') return null;
    const isOperator = state.me.kind === 'operator';
    return {
      me: state.me,
      warehouses: state.warehouses,
      warehouseId,
      isOperator,
      isSupervisor: isOperator && (state.me.role === 'admin' || state.me.role === 'supervisor'),
      isAdmin: isOperator && state.me.role === 'admin',
      setWarehouseId: (id: string) => {
        store.set(WAREHOUSE_KEY, id);
        setWarehouse(id);
      },
      reload,
      renew: (token: string) => {
        setToken(token, logout);
        store.set(TOKEN_KEY, token);
      },
      logout,
    };
  }, [state, warehouseId, logout, reload]);

  if (state.kind === 'loading') return <>{props.loading}</>;
  if (state.kind === 'unreachable') {
    return (
      <div className="login">
        <div className="card login-card">
          <div className="card-b stack">
            <div className="banner warn">
              <div>
                <div className="big">Sunucuya bağlanılamıyor</div>
                {state.message} Birkaç saniyede bir kendiliğinden yeniden deneniyor; oturumunuz kapanmadı.
              </div>
            </div>
            <button
              className="btn primary big block"
              onClick={() => {
                const saved = store.get(TOKEN_KEY);
                if (saved) void restore(saved);
              }}
            >
              Tekrar dene
            </button>
            <button className="btn ghost sm" style={{ alignSelf: 'center' }} onClick={logout}>
              Çıkış yap
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!value) return <>{props.login(start)}</>;
  return <SessionContext.Provider value={value}>{props.children}</SessionContext.Provider>;
}
